<template>
  <div class="salesforce-form">
    <m-content
      v-if="!getReportDetailsLoading && hasError"
      :padding-top="0"
      :padding-x="11"
      :padding-bottom="2"
    >
      <data-source-query-error
        :data-source="dataSource"
        :error="error"
      />
    </m-content>
    <template v-if="!hasConnectionError">
      <m-content
        :padding-top="0"
        :padding-x="11"
        :padding-bottom="2"
      >
        <div class="_item">
          <div class="_label">
            {{ $t('salesforce.selectReportLabel') }}
          </div>
          <m-select
            v-model:value="localQuery.reportId"
            :items="dataSource[customDatasourceProperty.salesforceReports]"
            value-key="id"
            item-text="name"
            full-width
            match-trigger-width
            show-search
            @change="resetFieldSelect"
          />
        </div>
        <template
          v-if="localQuery.reportId !== null && dataSource[customDatasourceProperty.salesforceReports].length > 0"
        >
          <div class="_item">
            <div class="_label">
              {{ $t('salesforce.selectFieldLabel') }}
            </div>
            <m-select
              v-model:value="localQuery.reportFieldId"
              :items="fields"
              value-key="identifier"
              item-text="label"
              full-width
              match-trigger-width
              show-search
              @change="changeReportField"
            />
          </div>
        </template>
        <div
          v-if="localQuery.reportId !== null"
          class="_item"
          :style="{ marginTop: '2rem' }"
        >
          <always-sync-checkbox v-model:value="localQuery.alwaysSync" />
        </div>
      </m-content>
      <m-divider xs />
      <template v-if="localQuery.uid === undefined">
        <m-content padding-small>
          <div class="_actions">
            <div class="_btns">
              <m-btn
                class="_btn"
                @click="$emit('close')"
              >
                {{ $t('general.cancel') }}
              </m-btn>
              <m-btn
                color="primary"
                class="_btn"
                :loading="mutateLoading"
                :disabled="!isValid"
                @click="save"
              >
                {{ $t('general.save') }}
              </m-btn>
            </div>
          </div>
        </m-content>
      </template>
      <template v-else>
        <m-card-item
          icon="save"
          :loading="mutateLoading"
          :padding-x="8"
          :disabled="!isValid"
          @click="save"
        >
          {{ $t('general.save') }}
        </m-card-item>
        <m-card-item
          icon="sync"
          :loading="syncLoading"
          :padding-x="8"
          :disabled="!isValid || hasError"
          @click="syncNow"
        >
          {{ $t('dataSource.syncNow') }}
        </m-card-item>
        <m-card-item
          icon="delete"
          :padding-x="8"
          :loading="deleteLoading"
          @click="deleteSalesforceQuery"
        >
          {{ $t('general.delete') }}
        </m-card-item>
      </template>
    </template>
  </div>
</template>

<script>
import AlwaysSyncCheckbox from '@/components/datasource/AlwaysSyncCheckbox.vue';
import DataSourceQueryError from '@/components/datasource/DataSourceQueryError.vue';
import useSalesforce from '@/composables/integrations/salesforce/salesforce';
import { camelCase } from 'lodash-es';
import { computed } from 'vue';
import { copy } from 'shared/lib/copy';
import { customDatasourceProperty, dataSourceQueryGenericError } from 'shared/constants.json';
import { isNullOrUndefined } from 'shared/lib/object/object';
import { logCatch } from '@/lib/logger/logger';

const defaultSalesforceQuery = () => ({
  reportId: null,
  reportName: null,
  reportFieldId: null,
  reportFieldName: null,
});

export default {
  name: 'SalesforceForm',
  props: {
    dataSource: {
      type: Object,
      required: true,
    },
    query: {
      type: Object,
      default: () => null,
    },
    goal: {
      type: Object,
      required: true,
    },
  },
  emits: ['close', 'deleted'],
  components: { DataSourceQueryError, AlwaysSyncCheckbox },
  setup() {
    const {
      createQuery,
      createQueryLoading,
      updateQuery,
      updateQueryLoading,
      deleteQuery,
      deleteQueryLoading,
      getReportDetails,
      getReportDetailsLoading,
    } = useSalesforce();

    return {
      createQuery,
      updateQuery,
      deleteQuery,
      mutateLoading: computed(() => createQueryLoading.value || updateQueryLoading.value),
      deleteLoading: deleteQueryLoading,
      getReportDetails,
      getReportDetailsLoading,
    };
  },
  data() {
    return {
      localQuery: { ...defaultSalesforceQuery() },
      isDirty: false,
      camelCase,
      syncLoading: false,
      customDatasourceProperty,
    };
  },
  computed: {
    hasConnectionError() {
      return [dataSourceQueryGenericError.incompleteSetup, dataSourceQueryGenericError.unauthenticated].includes(this.dataSource[customDatasourceProperty.dataSourceError]);
    },
    hasDataSourceError() {
      return !isNullOrUndefined(this.dataSource[customDatasourceProperty.dataSourceError]);
    },
    hasQueryError() {
      return !this.isDirty && !isNullOrUndefined(this.query?.error);
    },
    hasError() {
      return this.hasDataSourceError || this.hasQueryError;
    },
    error() {
      if (this.hasDataSourceError) {
        return this.dataSource[customDatasourceProperty.dataSourceError];
      }
      if (this.hasQueryError) {
        return this.query?.error;
      }
      return null;
    },
    isValid() {
      return this.selectedReport !== null && this.selectedField !== null;
    },
    fields() {
      if (this.localQuery.reportId === null) {
        return [];
      }

      const report = this.dataSource[customDatasourceProperty.salesforceReports].find((r) => r.id === this.localQuery.reportId);
      if (typeof report === 'undefined') {
        return [];
      }

      if (report.fields === undefined) {
        return [];
      }

      return report.fields;
    },
    selectedReport() {
      if (this.localQuery.reportId === null) {
        return null;
      }

      const report = this.dataSource[customDatasourceProperty.salesforceReports].find((r) => r.id === this.localQuery.reportId);
      if (report === undefined) {
        return null;
      }

      return report;
    },
    selectedField() {
      if (this.localQuery.reportFieldId === null) {
        return null;
      }

      if (this.selectedReport === null) {
        return null;
      }

      if (this.selectedReport.fields === undefined) {
        return null;
      }

      const field = this.selectedReport.fields.find((f) => f.identifier === this.localQuery.reportFieldId);
      if (field === undefined) {
        return null;
      }
      return field;
    },
  },
  methods: {
    save() {
      if (this.localQuery.uid === undefined) {
        this.createSalesforceQuery(this.localQuery);
        return;
      }

      this.updateSalesforceQuery(this.localQuery);
    },
    createSalesforceQuery(salesforceQuery) {
      this.createQuery({
        ...salesforceQuery,
        reportName: this.selectedReport.name,
        reportFieldName: this.selectedField.label,
        dataSource: { uid: this.dataSource.uid },
        goal: { uid: this.goal.uid },
      }).then((query) => {
        this.localQuery = copy(query);
        this.isDirty = false;
      }).catch(logCatch(() => {
        this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
      }));
    },
    updateSalesforceQuery(salesforceQuery) {
      this.updateQuery({
        ...salesforceQuery,
        reportName: this.selectedReport.name,
        reportFieldName: this.selectedField.label,
        dataSource: { uid: this.dataSource.uid },
      }).then((query) => {
        this.localQuery = copy(query);
        this.isDirty = false;
      }).catch(logCatch(() => {
        this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
      }));
    },
    syncNow() {
      this.syncLoading = true;
      this.updateQuery(this.goal.salesforceQuery).then((query) => {
        this.localQuery = copy(query);
        this.isDirty = false;
        this.$showSnackbar({ color: 'success', message: this.$t('dataSource.successfullySynced') });
      }).catch(logCatch(() => {
        this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
      })).finally(() => {
        this.syncLoading = false;
      });
    },
    deleteSalesforceQuery() {
      const deleteMethod = () => this.deleteQuery(this.goal.salesforceQuery.uid).then(() => {
        this.$emit('deleted');
        this.$emit('close');
      }).catch(() => {
        this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
      });

      this.$confirm({
        title: this.$t('dataSource.deleteConnectionPrompt'),
        okText: this.$t('general.yesDelete'),
        okType: 'danger',
        maskClosable: true,
        cancelText: this.$t('general.cancel'),
        onOk() {
          deleteMethod();
        },
      });
    },
    resetFieldSelect() {
      this.localQuery.reportFieldId = null;
      this.localQuery.reportFieldName = null;
      this.isDirty = true;
    },
    changeReportField() {
      this.isDirty = true;
    },
  },
  watch: {
    selectedReport(newReport, oldReport) {
      if (oldReport !== null && newReport.id === oldReport.id) {
        return;
      }

      this.getReportDetails(this.dataSource, newReport).catch(() => {
        this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
      });
    },
  },
  created() {
    if (this.query !== null) {
      this.localQuery = copy(this.query);
    }
  },
};
</script>

<style scoped lang="scss" type="text/scss">
.salesforce-form {
  ._item {
    margin-bottom: 1.4rem;

    ._label {
      margin-bottom: .4rem;
      color: $font-color-secondary;
    }
  }

  ._actions {
    display: flex;

    ._btns {
      display: flex;
      margin-left: auto;

      ._btn {
        margin-left: .4rem;
      }
    }
  }
}
</style>
